import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";

import Layout from "./Layout";
import PageLoaderComp from "../component/pageLoaderComp";
import { 
  PageHeader, 
  StatusBadge, 
  ConfirmModal,
  ResponsiveDataTable,
  getDossierDisplayType,
  getLegalFormType,
  getDossierActionUrl,
} from "./Components";

/**
 * Refactored Mes Dossiers Component
 * - Uses ResponsiveDataTable for modern, no-horizontal-scroll design
 * - Card-based layout on mobile
 * - Clean grid layout on desktop
 */
export default function MesDossiersRefactored() {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentUser = useSelector((state) => state.authentication.user);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchDossiers = async () => {
      if (!currentUser?.data?.id) return;
      
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}api/prouser/dossiers_all/${currentUser.data.id}`
        );

        if (response.status === 200 && response.data.success) {
          setDossiers(response.data.data);
        }
      } catch (err) {
        setError("Erreur lors du chargement des dossiers");
        console.error("Error fetching dossiers:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDossiers();
  }, [currentUser]);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (error) {
    return (
      <Layout>
        <div className="mb-8 col-span-12 xl:col-span-9">
          <div className="xl:max-w-[1126px] w-full h-full bg-white md:rounded-[20px] p-6">
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const handleLinkClick = (e, url) => {
    e.preventDefault();
    setSelectedUrl(url);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    window.open(selectedUrl, "_blank");
    setShowConfirmModal(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderActionButton = (item) => {
    if (item.status === 0) {
      const url = getDossierActionUrl(item);
      return (
        <button
          onClick={(e) => handleLinkClick(e, url)}
          className="text-sm font-medium text-white px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 transition-colors whitespace-nowrap"
        >
          Compléter
        </button>
      );
    }
    if (item.status === 1 || item.status === 2) {
      return (
        <a
          href={`/complete-formalities/${item.id}`}
          className="text-sm font-medium text-white px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 transition-colors whitespace-nowrap inline-block"
        >
          {item.status === 1 ? "Compléter" : "En cours"}
        </a>
      );
    }
    return (
      <a
        href={`/complete-formalities/${item.id}`}
        className="text-sm font-medium text-white px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 transition-colors whitespace-nowrap inline-block"
      >
        Finalisé
      </a>
    );
  };

  // Table columns configuration
  const columns = [
    {
      header: 'Dossier',
      field: 'formtype',
      render: (item) => {
        const displayType = getDossierDisplayType(item);
        const legalFormType = getLegalFormType(item);
        return <span className="font-medium">{displayType} {legalFormType}</span>;
      },
    },
    {
      header: 'Entreprise',
      field: 'entreprisename',
      render: (item) => item.entreprisename || '-',
      truncate: true,
    },
    {
      header: 'Créé le',
      field: 'created_at',
      render: (item) => moment(item.created_at).format('DD/MM/YYYY'),
    },
    {
      header: 'Statut',
      field: 'status',
      render: (item) => <StatusBadge status={item.status} type="dossier" />,
    },
    {
      header: 'Action',
      field: 'action',
      isAction: true,
      render: (item) => renderActionButton(item),
    },
  ];

  return (
    <>
      {loading && <PageLoaderComp />}
      
      <Layout>
        <div className="mb-8 col-span-12 xl:col-span-9">
          <div className="xl:max-w-[1126px] w-full h-full bg-white md:rounded-[20px]">
            
            {/* Header */}
            <PageHeader title="Mes Dossiers">
              <a
                href={`${process.env.REACT_APP_EXPERT_URL}?id=${currentUser.token}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:bg-blue-600 text-white sm:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <span className="text-base sm:text-xl">+</span>
                <span className="text-base sm:text-xl">Nouveau dossier</span>
              </a>
            </PageHeader>

            {/* Responsive Data Table - No horizontal scroll */}
            <div className="p-4 md:p-6">
              <ResponsiveDataTable
                columns={columns}
                data={dossiers}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                renderActions={renderActionButton}
                emptyMessage="Aucun dossier trouvé"
                keyField="id"
              />
            </div>
          </div>
        </div>
      </Layout>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        title="Confirmation"
        message="Êtes-vous sûr de vouloir continuer vers la form de modification ?"
        secondaryMessage="Des frais supplémentaires peuvent s'appliquer si les modifications affectent les conditions de paiement d'origine."
      />
    </>
  );
}
